/*
 *  Product Template
 *    Scripts for the product template section
 *
 *  Version:
 *    1.1.0 - 2022/10/06
 */

import {CLASS_HAS_COMPARE, CLASS_HAS_ERROR, CLASS_HIDDEN, CLASS_SELECTED} from '../../tools/constants/classes';
import * as Language from '../../tools/constants/language';
import { Variant } from '../../tools/product/variant';
import { getAllSelectedOptions, getallSelectedOptionsObjects, getIsNotExistent, swatchesFind } from '../../components/product/product-swatches';
import { getVariant, variantSelectorSetVariant, variantSelectorWatch } from "../../objects/product/variant-selector";
import {
  printMoney, t, setQueryParams, cartAdd, getCountOfVariantInCart,
  ON_CART_FINISHED
} from '@process-creative/slate-theme-tools';
import { openModal } from '../modal';
import { initAccordion } from '../accordion';
import './../../objects/product/quantity-selector-component';
import { QuantitySelectorElement } from './../../objects/product/quantity-selector-component';
import { ButtonAddToCart, formatMoneyCurrency, handleize } from './product-utils';

// Attributes
const ATTR_MONEY = 'data-money';
const ATTR_NOTIFY_BTN = 'data-notify-btn';

// Selectors
const SELECTOR_TEMPLATE = '[data-product-template]';
const SELECTOR_PRICING = '[data-product-pricing]';
const SELECTOR_PRICE = '[data-product-money]';
const SELECTOR_PRICE_COMPARE = '[data-product-compare-money]';
const SELECTOR_ACCORDIONS = '[data-accordion-container]';
const SELECTOR_BIS_FORM = '[data-bis-form]';
const SELECTOR_SIZE_GUIDE_LINK = '[data-size-guide-link]'
const SELECTOR_QUANTITY_SELECTOR = '[data-quantity-selector]';
const SELECTOR_SWATCH_MESSAGE = '[data-selector-swatch-error]';
const SELECTOR_SWATCH_OPTION_NAME = '[data-swatch-option-name]';
const SELECTOR_ALT_PRICE = "[data-alternate-money-price]";

const win = window as Window & typeof globalThis & {Currency: {currency: string}}
const currency = win.Currency.currency || 'AUD'
const format = 'money_with_currency_format'

// Initialise template
document.querySelectorAll<HTMLElement>(SELECTOR_TEMPLATE).forEach(template => {
  // Elements
  const pricingElement = template.querySelector<HTMLElement>(SELECTOR_PRICING);
  const priceElement = template.querySelector<HTMLElement>(SELECTOR_PRICE);
  const afterpayElement = template.querySelector<HTMLSpanElement>(SELECTOR_ALT_PRICE);
  const priceCompareElement = template.querySelector<HTMLElement>(SELECTOR_PRICE_COMPARE);
  const buttonAddToCart = new ButtonAddToCart(template);  
  const bisForm = template.querySelector<HTMLFormElement>(SELECTOR_BIS_FORM);
  const accordionContainer = template.querySelector<HTMLDivElement>(SELECTOR_ACCORDIONS);
  const quantitySelector = template.querySelector<QuantitySelectorElement>(SELECTOR_QUANTITY_SELECTOR);
  const messageBox = template.querySelector<HTMLDivElement>(SELECTOR_SWATCH_MESSAGE);
  const swatchContainer = template.querySelector<HTMLDivElement>('[data-swatches]');

  let swatchErrorString = ''
  if(accordionContainer) initAccordion(accordionContainer);

  if (messageBox) {
    swatchErrorString = messageBox?.innerHTML || ""
  }

  setupSizeGuide();

  const updatePricing = (variant:Variant) => {
    if(!priceElement) return;

    let { price, compare_at_price } = variant;

    // Adjust price display
    if(!price || price <= 0) {
      priceElement.removeAttribute(ATTR_MONEY);
      priceElement.innerText = t(Language.PRODUCT_FREE);
    } else {
      priceElement.setAttribute(ATTR_MONEY, price.toString());
      // priceElement.innerHTML = printMoney(price, format, currency).replace('.00','');
      priceElement.innerHTML = `${formatMoneyCurrency(price)}`;
    }


    // Adjust compare price display
    if(!priceCompareElement || !pricingElement) return;

    if(!compare_at_price || compare_at_price <= 0) {
      pricingElement.classList.remove(CLASS_HAS_COMPARE);
      priceCompareElement.removeAttribute(ATTR_MONEY);
      priceCompareElement.innerText = '';
      if(afterpayElement){
        const afterPaynumber = price/4;
        afterpayElement.setAttribute(ATTR_MONEY,afterPaynumber.toString())
        // afterpayElement.innerHTML = printMoney(afterPaynumber, format, currency).replace('.00','');
        afterpayElement.innerHTML = `${formatMoneyCurrency(afterPaynumber)}`;
      }
    } else {
      pricingElement.classList.add(CLASS_HAS_COMPARE);
      priceCompareElement.setAttribute(ATTR_MONEY, compare_at_price.toString());
      // priceCompareElement.innerHTML = printMoney(compare_at_price, format, currency).replace('.00','');
      priceCompareElement.innerHTML = `${formatMoneyCurrency(compare_at_price)}`;
      if(afterpayElement){
        const afterPaynumber = compare_at_price/4;
        afterpayElement.setAttribute(ATTR_MONEY,afterPaynumber.toString())
        // afterpayElement.innerHTML = printMoney(afterPaynumber, format, currency).replace('.00','');
        afterpayElement.innerHTML = `${formatMoneyCurrency(afterPaynumber)}`;
      }
    }
  }

  const onVariantChange = (variant:Variant, updateUrl:boolean = true) => {
    const missingSelect = unselectedSwatches();
    let option = '';
    const pendinOptions: string[] = []
    missingSelect.forEach(swatch => {
      const selectedOption: HTMLDivElement | null = swatch.querySelector(SELECTOR_SWATCH_OPTION_NAME);
      if (selectedOption) {
        const name = selectedOption?.dataset.swatchOptionName?.toLowerCase();
        pendinOptions.push(`${name}`);
      }
    });
    if (missingSelect.length > 0) {
      const missingPositions:string[] = [];
      missingSelect.forEach(element => {
        const positionElement: HTMLDivElement = (element.querySelector('[data-swatch-position]')) as HTMLDivElement;
        if (positionElement) {
          if (typeof positionElement.dataset.swatchPosition !== 'undefined') {
            const position = positionElement.dataset.swatchPosition;
            missingPositions.push(position);
          }
        }
      });
      
      if (variant.available === false) {
        const findAvailableVariants = swatches?.variantSelector.product.variants.filter(v => {
          const selectedOptions = swatches?.variantSelector.product.options.map(option => String(option.position)).filter(a => !missingPositions.includes(a));
          let isAMatch = false;
          if (selectedOptions.length == 0) {
            const colourPosition = swatches?.variantSelector.product.options.find(option => option.name === 'Color' || option.name === 'Colour')?.position || -1;
            if (colourPosition > -1) {
              if (v.options[colourPosition - 1] === variant.options[colourPosition - 1]) {
                isAMatch = true;
              }
            }
          } else {
            isAMatch = true;
            selectedOptions.forEach(option => {
              switch (option) {
                case '1': 
                  if (v.option1 && v.option1 !== variant.option1) {
                    isAMatch = false;
                  }
                case '2':
                  if (v.option2 && v.option2 !== variant.option2) {
                    isAMatch = false
                  }
                case '3':
                  if (v.option3 &&  v.option3 !== variant.option3) {
                    isAMatch = false
                  }
              }
            });
          }

          return isAMatch && v.available === true;
        });
        if (findAvailableVariants && findAvailableVariants?.length > 0) {
          const findAvailableVariant = findAvailableVariants.sort((a,b) => a.price - b.price);
          variant = findAvailableVariant[0];
        } else {
          const colourPosition = swatches?.variantSelector.product.options.find(option => option.name === 'Color' || option.name === 'Colour')?.position || -1;
          const colourHandle = handleize(variant.options[colourPosition - 1] || '');
          document.querySelector(`button[data-swatch-position="${colourPosition}"][data-value="${colourHandle}"]`)?.classList.add('is-sold-out');
        }
      }
    }
    // Update pricing
    updatePricing(variant);

    // Update add to cart
    updateAddToCart(variant);

    updateQtySelector(variant);

    //Display markdown message when the variant price varies.
    const markDownElements:NodeListOf<HTMLElement> = document.querySelectorAll('[data-markdown-message]');
    if (markDownElements.length > 0) {
      const showMarkDownOnSaleOnly = markDownElements[0].dataset.markdownMessageOnSale;
      if (showMarkDownOnSaleOnly === "true") {
        if (variant.compare_at_price && variant.price < variant.compare_at_price) {
          markDownElements.forEach(markDownElement => {
            markDownElement.dataset.markdownMessage = "true";
          })
        } else {
          markDownElements.forEach(markDownElement => {
            markDownElement.dataset.markdownMessage = "false";
          })
        }
      }
    }
    
    // Update url
    if (updateUrl) {
      let url = setQueryParams({ variant: variant.id });
      let variantId = variant.id;
      history.replaceState({ variant, variantId }, document.title, url);    
    }
  }

  const updateAddToCart = (variant:Variant) => {
    if(!buttonAddToCart.exists()) return;
    buttonAddToCart.reset();

    // Adjust the add to cart button based on QTY available and in cart
    let inCart = getCountOfVariantInCart(variant.id);
    let { available, inventory_policy, inventory_management, inventory_quantity } = variant;
    if(!inventory_quantity) inventory_quantity = 0;
    if(available) {
      if(inventory_policy != 'continue' && inventory_management != null && (inventory_quantity - inCart) <= 0) {
        buttonAddToCart.btnAllInCart();
      } else {
        buttonAddToCart.btnEnable();
      }
    } else if(bisForm) {
      buttonAddToCart.btnNotify();
    } else {
      buttonAddToCart.btnDisable();
    }
  }

  const updateQtySelector = (variant:Variant) => {
    if(!quantitySelector) return;
    const inCart:number = getCountOfVariantInCart(variant);
    const inventoryQty:number = variant.inventory_quantity || 0;
    if(!inCart && inventoryQty) {
      quantitySelector.max = inventoryQty - inCart;
    }
  }

  // Swatches
  const swatches = swatchesFind({ container: template });
  if(!swatches) return;
  
  window.addEventListener('product-swatch-selected', (e) => {
    const customEvent = (e as CustomEvent);
    hideErrorMessage();
    if (getIsNotExistent() === true) {
      buttonAddToCart.btnDisable();
    } else {
      if (customEvent.detail.variant) {
        onVariantChange(customEvent.detail.variant);
      }
    }
  });
  
  // On variant change
  variantSelectorWatch({
    variantSelector: swatches.variantSelector,
    variantChange: ({ variant }) => {
      hideErrorMessage();
      onVariantChange(variant);
    }
  });

  const allSelectedOptionsObjects = getallSelectedOptionsObjects(swatches.objects);
  const allSelectedOptions = getAllSelectedOptions(allSelectedOptionsObjects);

  if (allSelectedOptions.length === 0) {
    // If now option is selected we want to show the cheapest variant, this is required because the url of the pdp product might have a variant id, 
    // if the variant id is from a product that is more expensive
    // the customer will get the wrong idea that the price is randomly shown
    const { variantSelector } = swatches;
    const smallestPriceVariant = variantSelector.product.variants.reduce((prev, current) => current.price < prev.price ? current : prev)
    if (smallestPriceVariant) {
      const { product } = variantSelector;
      // variantSelectorSetVariant({
      //   variantSelector, variantId: smallestPriceVariant.id, product
      // });
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const variantParam = urlParams.get('variant');

  // We validate if there is a variant param in the url to avoid forcing the replacement of the default variant as this could cause confusing to the customer where the add to cart will be available but the preselected options are not the desired ones
  if (!variantParam) {
    swatches.variantSelector.variant = swatches.variantSelector.product.variants[0];
    const forcedDefaultVariant = buttonAddToCart?.buttonAdd?.dataset.addToCartButton;
    if (forcedDefaultVariant) {
      const availableForcedVariant = swatches.variantSelector.product.variants.find(variant => {
        return variant.id == Number(forcedDefaultVariant) && variant.available;
      });
      if (availableForcedVariant) {
        swatches.variantSelector.variant = availableForcedVariant;
      }
    } else {
      if (!swatches.variantSelector.variant.available) {
        const colourOptionIndex = swatches.variantSelector.product.options.findIndex(option => option.name === 'Colour' || option.name === 'Color');
        const currentColor = colourOptionIndex === 0 ? swatches.variantSelector.variant.option1 : colourOptionIndex === 1 ? swatches.variantSelector.variant.option2: swatches.variantSelector.variant.option3;
        const firstAvailableVariants = swatches.variantSelector.product.variants.filter(variant => {
          let isAColourMatch = false;
          if (colourOptionIndex === 0 && variant.option1 === currentColor) {
            isAColourMatch = true;
          }
          else if (colourOptionIndex === 1 && variant.option2 === currentColor) {
            isAColourMatch = true;
          } else if (variant.option3 === currentColor) {
            isAColourMatch = true;
          }
          variant.available === true && isAColourMatch
        });
        if (firstAvailableVariants.length > 0) {
          const firstAvailableVariant = firstAvailableVariants.sort((a, b) => a.price - b.price)
          if (firstAvailableVariant) {
            swatches.variantSelector.variant = firstAvailableVariant[0];          
          }
        }
      }
    }
  } else {
    if (!swatches.variantSelector.variant.available) {
      const colourOptionIndex = swatches.variantSelector.product.options.findIndex(option => option.name === 'Colour' || option.name === 'Color');
      const currentColor = colourOptionIndex === 0 ? swatches.variantSelector.variant.option1 : colourOptionIndex === 1 ? swatches.variantSelector.variant.option2: swatches.variantSelector.variant.option3;
      const firstAvailableVariants = swatches.variantSelector.product.variants.filter(variant => {
        let isAColourMatch = false;
        if (colourOptionIndex === 0 && variant.option1 === currentColor) {
          isAColourMatch = true;
        }
        else if (colourOptionIndex === 1 && variant.option2 === currentColor) {
          isAColourMatch = true;
        } else if (variant.option3 === currentColor) {
          isAColourMatch = true;
        }
        variant.available === true && isAColourMatch
      });
      if (firstAvailableVariants.length > 0) {
        const firstAvailableVariant = firstAvailableVariants.sort((a, b) => a.price - b.price)
        if (firstAvailableVariant) {
          swatches.variantSelector.variant = firstAvailableVariant[0];          
        }
      }
    }
  }

  // Set initial button & QTY states
  onVariantChange(swatches.variantSelector.variant, false);
  
  // The following was added to improve the experience on fully unavailable products
  if (!swatches.variantSelector.product.available) {
    const firstColourSwatch:HTMLButtonElement = template.querySelector('button[data-swatch-position="1"]') as HTMLButtonElement;
    if (firstColourSwatch) {
      firstColourSwatch.click();
      firstColourSwatch.classList.remove('is-selected');
      firstColourSwatch.classList.add('is-sold-out');
    }
  }

  // Add to cart button click
  if(buttonAddToCart.exists()) {
    let atcState:'adding'|'added'|'pending'|'error' = 'pending';
    buttonAddToCart.get()?.addEventListener('click', async e => {
      e.preventDefault();
      const variant = swatches.variantSelector.variant;
      if(buttonAddToCart.get()?.hasAttribute(ATTR_NOTIFY_BTN)){
        showNotifyBackInStockModal()
        return
      }

      // This updates the error message when not all the options have been selected
      if(!allSwatchesSelected()) {
        const missingSelect = unselectedSwatches();
        let option = '';
        const pendinOptions: string[] = []
        missingSelect.forEach(swatch => {
          const selectedOption: HTMLDivElement | null = swatch.querySelector(SELECTOR_SWATCH_OPTION_NAME);
          if (selectedOption) {
            const name = selectedOption?.dataset.swatchOptionName?.toLowerCase();
            pendinOptions.push(`${name}`);
          }
        })
        option = pendinOptions.join(' and ');
        return showErrorMessage(option);
      }

      const qty:number = quantitySelector?.quantity ? quantitySelector.quantity : 1;

      if(atcState !== 'pending' || !variant.available || qty <= 0) return;

      atcState = 'adding';
      buttonAddToCart.btnDisable();
      buttonAddToCart.loading();      

      try {
        let params = { items: [{ id:variant.id, quantity: qty }]}
        await cartAdd(params);
        atcState = 'added';
        buttonAddToCart.added();
      } catch(e:any) {
        alert(e.description);
        atcState = 'error';
        buttonAddToCart.error();
        console.error(e);
      }

      setTimeout(() => {
        updateAddToCart(variant)
        atcState = 'pending';
      }, 3500);
    });
  }

  function setupSizeGuide(){
    const sizeGuideLink = template.querySelector(SELECTOR_SIZE_GUIDE_LINK)!
    sizeGuideLink?.addEventListener('click', _ =>  openModal({modalContentName: 'sizeChart', maxWidth: 760}))
  }

  function showNotifyBackInStockModal(){
    openModal({modalContentName: 'backInStock'})
  }

  function unselectedSwatches() {
    const swatchObjects = template.querySelectorAll<HTMLDivElement>('[data-swatch]');
    return Array.from(swatchObjects).filter(obj => {
      //Other objects, return true if selected or it's a select elements
      return !obj.querySelector(`.${CLASS_SELECTED}`);
    });
  }

  function allSwatchesSelected() {
    const swatchObjects = template.querySelectorAll<HTMLDivElement>('[data-swatch]');
    return Array.from(swatchObjects).every(obj => {
      return (
        obj.querySelector(`.${CLASS_SELECTED}`) ||
        obj.querySelector<HTMLSelectElement>('[data-swatch-selector]')
      );
    });
  }

  function hideErrorMessage() {
    if(!messageBox || !swatchContainer) return;
    messageBox.innerHTML = '';
    swatchContainer.classList.remove(CLASS_HAS_ERROR);
    messageBox.classList.add(CLASS_HIDDEN);
  }

  function showErrorMessage(option: string){
    buttonAddToCart.btnNotSelected();
    
    if(!messageBox || !swatchContainer) return;
    messageBox.innerHTML = swatchErrorString.replace('{ option }', option) || "";
    swatchContainer.classList.add(CLASS_HAS_ERROR);
    messageBox.classList.remove(CLASS_HIDDEN);
  }

  /*
    On cart finished - update the button. This takes the cart drawer
    into account
  */
  document.addEventListener(ON_CART_FINISHED, () => updateAddToCart(swatches.variantSelector.variant))
});